<template>
	<div>
		<div v-if="!emailVerified || isExceededMaxBalance" id="errorContainer" class="alert alert-danger text-center" role="alert">
			<svgIcon icon-holder-class="icon-warning" icon-id="icon-warning" />

			<i18n v-if="!emailVerified" path="wallet.verify-email">
				<template v-slot:verifyLink>
					<a target="_blank" :href="accountSettingPageUrl">{{ $t('wallet.verify') }}</a>
				</template>
			</i18n>

			<span v-else-if="isExceededMaxBalance">
				{{
					$t('wallet.max-balance-restriction', {
						limit: region !== 'tw' ? $n(maxLimitAmount, 'currency', currency) : `${$n(maxLimitAmount)} ${$t('wallet.points')}`,
					})
				}}
			</span>
		</div>
		<div v-else-if="notification" id="errorContainer" class="alert alert-danger text-center" role="alert">
			<div class="error-section">
				<svgIcon icon-holder-class="icon-warning" icon-id="icon-warning" />
				<!-- eslint-disable-next-line vue/no-v-html -->
				<div v-html="notification"></div>
			</div>
		</div>

		<div class="add-balance wrapper">
			<div class="murk">
				<balanceInfo
					:title="region === 'global' ? $t('wallet.buy-nx') : $t('wallet.addBalance')"
					show-switch-account
					:switch-account-tracking="switchAccountTracking"
				></balanceInfo>

				<div class="select-pricepoint-wrapper">
					<h4
						v-t="region === 'sea' || region === 'th' ? 'wallet.select-amount-purchase' : 'wallet.select-nx-amount'"
						class="heading-normal title-add-balance"
					></h4>
					<p class="description-add-balance">
						{{ $t('wallet.mobile-warning') }}
						<br v-if="region === 'tw'" />
						<i18n path="wallet.prices-shown-in">
							<template v-slot:currency>{{ currency }}</template>
						</i18n>
					</p>
					<p v-if="twMaintenance" class="maintenance-add-balance">
						{{ $t('wallet.maintenance') }}
					</p>
					<p v-if="showRecentPurchaseDate" class="purchase-warning">
						{{
							$t('wallet.last-1-years-no-purchase', {
								date: localBaseTime,
							})
						}}
					</p>

					<div class="pricepoints-wrapper" :class="region">
						<template v-for="(product, index) in products">
							<!-- for west/th/sea display all the pricepoints but for TW only the pricepoints for which isDefault property === true -->
							<button
								v-if="region !== 'tw' || (region === 'tw' && product.isDefault)"
								:key="product.productId"
								:disabled="disabledPricepoints"
								class="btn btn-block btn-default pricepoint-button"
								:class="{ disabled: disabledPricepoints }"
								@click="!disabledPricepoints ? handleSelectedPricepoint(product, index + 1) : null"
							>
								<div
									v-if="region !== 'th' && region !== 'sea'"
									:class="`pricepoint-button__${region === 'global' ? 'nxAmount' : 'amount'}`"
								>
									{{ region === 'global' ? $n(product.nxAmount) : $n(product.amount) }}
								</div>
								<div :class="`pricepoint-button__${region === 'global' ? 'nx-img' : 'img'}`"></div>
								<div class="pricepoint-button__currency">{{ $n(product.amount, 'currency', product.currency) }}</div>
							</button>
						</template>

						<div v-if="region === 'tw'" class="pricepoint-dropdown">
							<formSelect
								v-model="dropdownSelectedPricepoint"
								class="form-select-pricepoint"
								:place-holder="$t('wallet.more-amounts')"
								:options="dropdownPricepointsOptions"
								options-value="productId"
								options-label="name"
								dont-localize
								:disabled="!emailVerified"
								@open="onOpenSelect"
							>
								<template v-slot:option="{ option }">
									<div class="option-item">
										<span>
											{{ option.amount }}
										</span>
										<span>
											{{ option.price }}
										</span>
									</div>
								</template>
							</formSelect>
						</div>
					</div>
				</div>

				<faqAccordion :get-epin-nexon-cards-token="getEpinNexonCardsToken" :disabled-payments="disabledPricepoints"></faqAccordion>

				<div class="modal" :class="{ hide: !loading }">
					<div id="main-spinner" class="page-spinner-wrapper main-spinner">
						<span class="spinner-circle"></span>
					</div>
				</div>
			</div>
		</div>

		<serviceTermination :show-modal="showServiceTerminationModal" />
	</div>
</template>

<script>
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import balanceInfo from '@/components/common/balance-info';
import faqAccordion from '@/components/common/faq-accordion';
import serviceTermination from '@/components/dialog/service-termination';
import config from '@/config/environment-config';
import { mapGetters } from 'vuex';
import formSelect from '@arena/toolkit/src/components/common/form-select';
import { getBalance, getProducts } from '../plugins/WalletAPI';

export default {
	name: 'BuyNx',
	components: {
		svgIcon,
		balanceInfo,
		faqAccordion,
		formSelect,
		serviceTermination,
	},
	metaInfo() {
		return {
			titleTemplate: `%s | ${this.region === 'global' ? this.$t('wallet.buy-nx') : this.$t('wallet.add-balance')}`,
		};
	},
	data() {
		return {
			accountSettingPageUrl: `${config[process.env.NODE_ENV][environment.region].account}/setting`,
			loading: false,
			dropdownSelectedPricepoint: null, //for tw region only
			baseDateTime: '',
			twMaintenance: false,
			showServiceTerminationModal: false,
			notification: '',
		};
	},
	computed: {
		...mapGetters({
			emailVerified: 'isVerifiedEmail',
			currency: 'currency',
			productType: 'productType',
			products: 'products',
		}),
		region() {
			return environment.region;
		},
		disabledPricepoints() {
			return !this.emailVerified || this.isExceededMaxBalance;
		},
		balanceData() {
			return this.$store.state.balanceData;
		},
		isExceededMaxBalance() {
			return this.region !== 'global' ? this.balanceData && this.balanceData.isExceededMaxBalance : false;
		},
		maxLimitAmount() {
			return this.balanceData && this.balanceData.maxLimitAmount;
		},
		dropdownPricepointsOptions() {
			if (this.region === 'tw') {
				return this.products
					.filter((item) => !item.isDefault)
					.map((item) => ({
						productId: item.productId,
						name: `${this.$n(item.amount)} ${this.$t('wallet.points')} - ${this.$n(item.amount, 'currency', item.currency)}`,
						amount: `${this.$n(item.amount)} ${this.$t('wallet.points')}`,
						price: this.$n(item.amount, 'currency', item.currency),
					}));
			} else {
				return [];
			}
		},
		showRecentPurchaseDate() {
			return this.region === 'global' && this.baseDateTime;
		},
		localBaseTime() {
			if (this.showRecentPurchaseDate) {
				let localTime = null;
				if (this.baseDateTime.toLowerCase().endsWith('z')) {
					localTime = new Date(this.baseDateTime);
				} else {
					localTime = new Date(`${this.baseDateTime}z`);
				}

				const year = localTime.getFullYear();
				const month = this.addDateLeftPad(localTime.getMonth() + 1);
				const day = this.addDateLeftPad(localTime.getDate());
				return `${year}-${month}-${day}`;
			}

			return '';
		},
	},
	watch: {
		dropdownSelectedPricepoint(val) {
			if (val) {
				this.selectedDropdownValue(val);
			}
		},
	},
	async mounted() {
		this.$a2s.view('imp');
		this.checkNotification();

		if (this.$store.state.region === 'th') {
			this.showServiceTerminationModal = true;
		}

		window.addEventListener('pageshow', this.pageshowEventHandler);
		try {
			const recentPurchaseRes = await this.$walletAPI.getRecentPurchaseDate();
			const data = recentPurchaseRes.data;
			if (!data.utcRecentTransactionDatetime) {
				this.baseDateTime = data.utcBaseDatetime;
			}

			if (this.$store.state.region === 'tw') {
				const response = await this.$walletAPI.getTWStatusCheck();
				this.twMaintenance = !!response.data;
			}
		} catch (e) {
			this.baseDateTime = '';
			this.twMaintenance = false;
		}
	},
	destroyed() {
		window.removeEventListener('pageshow', this.pageshowEventHandler);
	},
	methods: {
		handleCheckoutRedirection(token, method = null) {
			const checkoutWebBaseURL = config[process.env.NODE_ENV][environment.region].checkout;
			window.location = `${checkoutWebBaseURL}/${this.$i18n.locale}?token=${encodeURI(token)}&app_type=${this.$store.state.appType}${
				method ? `&method=${method}` : ''
			}`;
		},
		handleCheckoutErrorRedirection(error) {
			const checkoutWebBaseURL = config[process.env.NODE_ENV][environment.region].checkout;
			window.location = `${checkoutWebBaseURL}/en/failed${
				error.response && error.response.data && error.response.data.code ? `?error_code=${error.response.data.code}` : ''
			}`;
		},
		async handleSelectedPricepoint(pricepoint, buttonLocation) {
			const nxAmount = this.region === 'global' ? pricepoint.nxAmount : pricepoint.amount;
			this.$a2s.click('charge_select', {
				name: 'charge_select',
				value: `${pricepoint.name.replace(/\s+/g, '')}(${nxAmount.toLocaleString()})`,
				objectTag: 'purchase_start',
			});

			this.loading = true;

			dataLayer.push({
				event: 'Add Balance Select NX Amount',
				'NX Button Location': buttonLocation,
				'NX Amount': nxAmount,
			});

			dataLayer.push({ ecommerce: null }); // clear the ecommerce object per Ethan's recommendation and according to the google documentation https://developers.google.com/tag-manager/enhanced-ecommerce

			dataLayer.push({
				event: 'addToCart',
				ecommerce: {
					currencyCode: this.currency,
					add: {
						products: [
							{
								name: pricepoint.name,
								price: pricepoint.amount,
								quantity: 1,
								category: 'NX',
							},
						],
					},
				},
			});

			this.$walletAPI
				.getCheckoutToken({
					languageCode: this.$i18n.locale,
					productId: pricepoint.productId,
					productType: this.productType,
					returnURL: window.location.href,
				})
				.then((response) => {
					this.loading = false;
					if (this.dropdownSelectedPricepoint) this.dropdownSelectedPricepoint = null;
					this.handleCheckoutRedirection(encodeURI(response.data.token));
				})
				.catch((error) => {
					this.loading = false;
					if (this.dropdownSelectedPricepoint) this.dropdownSelectedPricepoint = null;
					this.handleCheckoutErrorRedirection(error);
				});
		},
		getEpinNexonCardsToken(methodCode) {
			this.loading = true;

			dataLayer.push({
				event: 'Add Balance Code Redemption Start',
				'Redemption Type': methodCode === 'PMLC04' ? 'nexoncards' : 'epin',
			});

			this.$walletAPI
				.getEpinNexonCardsToken({
					languageCode: this.$i18n.locale,
					selectedMethodCode: methodCode,
					returnURL: window.location.href,
				})
				.then((response) => {
					this.loading = false;
					this.handleCheckoutRedirection(encodeURI(response.data.token), methodCode === 'PMLC04' ? 'nexoncards' : 'epin');
				})
				.catch((error) => {
					this.loading = false;
					this.handleCheckoutErrorRedirection(error);
				});
		},
		selectedDropdownValue(value) {
			this.products.find((item) => {
				if (item.productId === value) {
					this.handleSelectedPricepoint(item, 0); // 'NX Button Location' should be 0 for all the pricepoints selected from the dropdown per data analyst request
				}
				return item.productId === value;
			});
		},
		onOpenSelect() {
			dataLayer.push({ event: 'clickedMoreAmounts' });
		},
		switchAccountTracking() {
			dataLayer.push({
				event: 'Add Balance Switch Account Click',
			});
		},
		addDateLeftPad(value) {
			if (value >= 10) {
				return `${value}`;
			}

			return `0${value}`;
		},
		pageshowEventHandler(e) {
			if (e.persisted) {
				// TODO: src\router\index.js beforeEnter 로직과 동일
				// 수정필요 시 함께 수정 필요
				Promise.all([getBalance(), getProducts()])
					.then((response) => {
						this.$store.commit('setBalanceData', response[0].data);
						this.$store.commit('setProductsData', response[1].data);
						if (response[1].data.account) this.$store.commit('setAccountData', response[1].data.account);
					})
					.catch((error) => {
						if (error.response && error.response.data && error.response.data.code) {
							if (error.response.data.code === '001-403-001') {
								window.location.replace(`${window.location.origin}/main/error?error=forbidden`);
							} else if (error.response.data.code !== '001-401-0001') {
								const checkoutWebBaseURL = config[process.env.NODE_ENV][environment.region].checkout;
								window.location = `${checkoutWebBaseURL}/en/failed?error_code=${error.response.data.code}`;
							}
						}
					});
			}
		},
		async checkNotification() {
			const notificationList = await this.$notificationAPI.getNotificationList();
			const findWallet = notificationList.find((item) => item.Name === 'Wallet');
			const locale = this.$route.params.locale;
			console.log(this.region);
			if (findWallet && findWallet.Message && locale && findWallet.Message[locale]) {
				this.notification = findWallet.Message[locale];
				return;
			}
			if (findWallet && findWallet.Message && this.region === 'global' && findWallet.Message.en) {
				this.notification = findWallet.Message.en;
			}
		},
	},
};
</script>

<style lang="less" scoped>
@import '~@arena/toolkit/src/less/toolkit/variables.less';
.errorContainer {
	position: relative;
	top: 0;
}
.alert {
	border-radius: 1px;
	&.tpa-warning {
		background-color: #bfbfbf;
		color: #000;
		// This is so the other warning can show properly spaced
		margin-bottom: 1px;
	}
}
#errorContainer {
	margin-bottom: 1px;
	&.blink-once {
		transition: all 0.5s ease-in-out;
		animation: blink normal 1.5s 1 ease-in-out;
	}
}
.error-section {
	display: flex;
	justify-content: center;
	> div {
		margin-bottom: -8px;
		padding-top: 2px;
	}
}

.select-pricepoint-wrapper {
	// min-height: 300px;
	text-align: center;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	.title-add-balance {
		flex: 0 0 100%;
		max-width: 100%;
		margin: 37px 0 5px;
		color: #fff;
		font-size: 24px;
	}

	.description-add-balance {
		flex: 0 0 100%;
		max-width: 100%;
		margin-bottom: 3px;
	}
	.maintenance-add-balance {
		flex: 0 0 100%;
		max-width: 100%;
		margin-bottom: 3px;
		color: #ff1744;
	}
	.purchase-warning {
		color: @brand-danger;
		margin-top: 1rem;
	}

	.pricepoints-wrapper {
		display: flex;
		flex-wrap: wrap;
		flex: 0 0 100%;
		max-width: 750px;
		.pricepoint-button {
			display: inline-flex;
			flex-wrap: wrap;
			// flex: 0 0 33.333333%;
			max-width: 230px;
			padding: 0;
			margin: 10px;
			height: auto;
			border: 1px solid rgb(#fff, 20%);
			border-radius: 2px;
			background-color: rgb(#fff, 5%);
			transition: background-color 0.3s;
			position: relative;
			color: #fff;
			font-size: 24px;
			font-weight: bold;
			justify-content: center;
			line-height: 1;
			&__nxAmount,
			&__amount {
				flex: 0 0 100%;
				max-width: 100%;
				display: flex;
				flex-wrap: wrap;
				justify-content: center;
				align-items: center;
				padding-top: 10px;
				&:before {
					content: '';
					display: inline-block;
					width: 30px;
					height: 30px;
					margin-right: 10px;
					background-size: contain;
					background-repeat: no-repeat;
					background-position: center;
				}
				img {
					margin-right: 10px;
				}
			}
			&__nxAmount {
				&:before {
					background-image: url('~@/images/nx-icons/nx-coin.png');
				}
			}
			&__amount {
				&:before {
					background-image: url('~@/images/coins/coin.png');
				}
			}
			&__img,
			&__nx-img {
				flex: 0 0 100%;
				max-width: 100%;
				height: 170px;
				display: inline-block;
				background-size: 170px 170px;
				background-repeat: no-repeat;
				background-position: center;
				margin-top: 7px;
				margin-bottom: 3px;
			}
			&__nx-img {
				background-image: url('~@/images/nx-icons/5000.png');
			}
			&__img {
				background-image: url('~@/images/coins/pricepoint1.png');
			}

			&__currency {
				flex: 0 0 100%;
				max-width: 100%;
				background-color: rgb(#000, 50%);
				padding: 7px 0;
			}

			&:hover,
			&:focus {
				border-color: rgb(#fff, 20%);
				background-color: rgb(#fff, 20%);
				color: #fff;
				transition: background-color 0.3s;
			}
			&:disabled,
			&.disabled {
				background-color: rgb(#fff, 5%);
				opacity: 0.3;
			}
			&:nth-child(2) {
				.pricepoint-button__img {
					background-image: url('~@/images/coins/pricepoint2.png');
				}
				.pricepoint-button__nx-img {
					background-image: url('~@/images/nx-icons/10000.png');
				}
			}
			&:nth-child(3) {
				.pricepoint-button__img {
					background-image: url('~@/images/coins/pricepoint3.png'); // TODO: replace image with non-west when will be ready
				}
				.pricepoint-button__nx-img {
					background-image: url('~@/images/nx-icons/20000.png');
				}
			}
			&:nth-child(4) {
				.pricepoint-button__img {
					background-image: url('~@/images/coins/pricepoint4.png'); // TODO: replace image with non-west when will be ready
				}
				.pricepoint-button__nx-img {
					background-image: url('~@/images/nx-icons/30000.png');
				}
			}
			&:nth-child(5) {
				.pricepoint-button__img {
					background-image: url('~@/images/coins/pricepoint5.png'); // TODO: replace image with non-west when will be ready
				}
				.pricepoint-button__nx-img {
					background-image: url('~@/images/nx-icons/50000.png');
				}
			}
			&:nth-child(n + 6) {
				.pricepoint-button__img {
					background-image: url('~@/images/coins/pricepoint6.png'); // TODO: replace image with non-west when will be ready
				}
				.pricepoint-button__nx-img {
					background-image: url('~@/images/nx-icons/100000.png');
				}
			}
		}

		.pricepoint-dropdown {
			flex: 0 0 100%;
			max-width: 100%;
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			text-align: left;
			margin-top: 14px;
			.form-select-pricepoint {
				max-width: 300px;
				width: 100%;
				:deep(.form-group) {
					margin-bottom: 0;
					.form-control,
					.dropdown-menu {
						background-color: #000;
					}
					.form-control {
						font-size: 16px;
						line-height: 1;
						border-radius: 2px;
						padding-left: 20px;
						padding-right: 20px;
						.btn-content {
							line-height: 1;
						}
						.caret {
							top: 37px;
						}
					}
					.dropdown-menu {
						height: auto;
						border-top: none;
						> li {
							> a {
								color: #fff;
								padding: 0 20px !important;
								&.highlight {
									background-color: transparent;
									color: #07f;
								}
								&:hover {
									background-color: transparent;
									color: #07f;
								}
							}
						}
						.option-item {
							display: flex;
							flex-wrap: wrap;
							justify-content: space-between;
							font-size: 18px;
							font-weight: bold;
							padding-top: 10px;
							padding-bottom: 10px;
							border-top: 1px solid rgba(#fff, 0.25);
						}
					}
					&.mobile {
						.placeholder {
							background-color: transparent;
							pointer-events: none;
						}
						select {
							option {
								color: #fff;
							}
						}
					}
					&.disabled {
						opacity: 0.3;
						.form-control {
							color: rgba(255, 255, 255, 0.5);
							border-color: rgba(255, 255, 255, 0.25);
						}
					}
				}
			}
		}

		&.th,
		&.sea {
			.pricepoint-button {
				&__img {
					margin-top: 17px;
				}
			}
		}
	}
}

.mobile,
.launcher {
	.modal {
		position: fixed;
	}
}

@media (max-width: 900px) {
	.select-pricepoint-wrapper {
		.pricepoints-wrapper {
			max-width: 500px;
		}
	}
}
@media (max-width: 599px) {
	.select-pricepoint-wrapper {
		.title-add-balance {
			margin-top: 12px;
		}

		.description-add-balance {
			font-size: 14px;
			line-height: 1.3;
			margin-top: 4px;
			margin-bottom: 11px;
		}
		.pricepoints-wrapper {
			margin: 0px;
			max-width: 100%;
			.pricepoint-button {
				flex: 0 0 100%;
				max-width: 100%;
				margin: 5px 0px;
				align-items: center;
				padding-top: 15px;
				padding-bottom: 15px;
				&__nxAmount,
				&__amount {
					flex-basis: 0;
					flex-grow: 1;
					max-width: 100%;
					padding: 0 10px 0 20px;
					justify-content: flex-start;
				}
				&__currency {
					flex: 0 0 auto;
					width: auto;
					max-width: 100%;
					padding: 0 20px 0 10px;
					background-color: transparent;
				}
				&__img,
				&__nx-img {
					background-image: none;
					display: none;
				}
			}
			.pricepoint-dropdown {
				margin-top: 0px;
				.form-select-pricepoint {
					max-width: 100%;
				}
			}
			&.th,
			&.sea {
				.pricepoint-button {
					&__currency {
						padding: 0 10px 0 10px;
					}
				}
			}
		}
	}
	.faq-wrapper {
		margin-top: 40px;
	}
}

@media (max-width: 400px) {
	.select-pricepoint-wrapper {
		.pricepoints-wrapper {
			.pricepoint-button {
				font-size: 22px;
				&__nxAmount,
				&__amount {
					padding: 0 0 0 20px;
				}
			}
		}
	}
}

@media (max-width: 359px) {
	.select-pricepoint-wrapper {
		.pricepoints-wrapper {
			.pricepoint-button {
				font-size: 18px;
			}
		}
	}
}
</style>
